import { z } from 'zod';
import { ulidSchema } from './id';
import { RANK_MAX_LENGTH } from './rank';

/**
 * Lists — the contract layer for block B11.
 *
 * `BE-11.1` (`FR-BOARD-009` add a list, `FR-BOARD-010` rename one),
 * `BE-11.2` (`FR-BOARD-011` reorder lists by dragging, which is SC-2's
 * lexorank landing on its first real table), `BE-11.3` (`FR-BOARD-015` archive
 * a list and restore it with its cards intact) and `BE-11.4` (`FR-BOARD-016`
 * permanently delete an archived list).
 *
 * **This is the first product content in this package.** Everything before it —
 * `./user`, `./workspace`, `./board` — is identity or permission, which is what
 * a product with no cards could honestly build. A list is the first thing on a
 * board that a member puts *work* into.
 *
 * Narrow on purpose, exactly as `./board` is B08/B09/B10's subset and
 * `./workspace` is B07's. **`BE-02.1` — the full Phase 1 object model (board,
 * list, card, comment, label, notification) — is a different item and stays
 * PENDING**, and this file is not its instalment either: what is here is what
 * B11's seven routes actually accept and return, added with the routes that use
 * it. A card is `BE-12.1`'s contract and appears nowhere below, not even as a
 * nested array — the board read that carries lists *and their cards* in one
 * payload is `BE-13.1`, which is a different item and is deliberately not built
 * here (`GET /boards/:id/lists` serves the board strip and the archive surface,
 * and nothing else). ⚠ **`BE-13.1` has since landed and this file still carries
 * no card**, which is the sentence above holding rather than expiring:
 * `./board-contents` composes `listSchema` with `.extend()` and imports
 * `cardSchema` from `./card`, so the nesting lives in the schema that needed it
 * and not one of B11's seven routes changed shape.
 *
 * The file follows the same division `./user`, `./workspace` and `./board`
 * follow, for the same reason: **input primitives**, which validate and
 * normalise what a route accepts, and **views**, which describe what a route
 * returns. An input primitive reused as a response field is the mistake the
 * division prevents — a response schema that trims returns a title the database
 * does not hold, and one that rejects refuses to serve a row that legitimately
 * exists.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT, so that its absence reads as a decision
 * ---------------------------------------------------------------------------
 *   - **Arbitrary decoration beyond `colour`.** B58 later made
 *     `lists.colour` a real wire field and mutation. Anything else decorative
 *     still needs the route that owns it before it crosses this schema.
 *   - **`archivedAt` and `deletedAt` as timestamps.** `archived` below is one
 *     boolean derived from `archived_at IS NOT NULL`, on `boardSchema.closed`'s
 *     exact footing and for its exact reasons. `deletedAt` is the stronger
 *     case: permanent deletion is a *soft* delete (`fk_cards_list_id` and
 *     `fk_activity_entries_card_id` are both `ON DELETE CASCADE`, so a hard
 *     `DELETE` of a list would take its cards and from there every activity
 *     entry those cards ever had — the loss SC-1 exists to prevent), every read
 *     path carries `deleted_at IS NULL`, and **no route anywhere writes it back
 *     to null**, so the field could only ever be `null` on the wire and would
 *     invite a client to write an unreachable `if (list.deletedAt)` branch that
 *     reads as though a restore path exists. It does not.
 *
 *     `archivedAt` is worth one extra sentence, because there is a requirement
 *     that looks like it needs it and does not. `FR-EXP-010` says the archived
 *     items surface is "ordered most-recently-archived first" — but that is an
 *     ordering the **server** applies to the rows it returns, not a key the
 *     client re-sorts by, so the timestamp does not have to cross the wire for
 *     the criterion to be met. If a later block gives the client a reason to
 *     re-sort archived lists itself, that block adds the field with the surface
 *     that needs it.
 *   - **The list's cards.** Not an array, not a nested view, not one card id.
 *     `cardCount` below is a number and is the only thing this file says about
 *     a list's contents — see its docstring for what it counts and why it is a
 *     count rather than a payload.
 *   - **Everything the four items do not build.** Moving a list to another
 *     board (`FR-BOARD-012`, V1) — which is why `createListBodySchema` takes a
 *     `boardId` and no patch below does; sorting a list's cards
 *     (`FR-BOARD-013`, V1); moving or archiving every card in a list at once
 *     (`FR-BOARD-014`, V1); watching a list (`FR-BOARD-017`, Parity-Later).
 *     Each would need a body of its own, and inventing one now is how
 *     `BE-02.1` quietly becomes unreviewable.
 *
 * Every width constant below is a real column width from
 * `db/postgres/migrations/0002_boards_lists_cards.sql`, stated next to the
 * column it comes from. Getting one wrong is not a type error — it is
 * `value too long for type character varying(n)` at INSERT time, surfaced as a
 * generic failure, which is the class of mistake this package exists to catch
 * at the boundary.
 */

// ---------------------------------------------------------------------------
// Column widths. One place to change when a migration widens a column.
// ---------------------------------------------------------------------------

/**
 * `lists.title varchar(512)`.
 *
 * The same width as `boards.title` and `cards.title`, and still declared here
 * rather than imported from `./board`: they are three independent columns that
 * three independent migrations could widen separately, and a shared constant
 * would make one of those widenings silently apply to all three. `./board`'s
 * own note asks `BE-12.1` to *import* `CARD_TITLE_MAX_LENGTH` because that
 * constant already describes `cards.title`; there is no constant describing
 * `lists.title` to import, so this is the one.
 */
export const LIST_TITLE_MAX_LENGTH = 512;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A list title as `lists.title` stores it.
 *
 * `.trim().min(1)` is `boardTitleSchema`'s idiom, reused rather than
 * re-decided, and here it *is* two requirements rather than a house
 * preference: `FR-BOARD-009`'s edge case — "a list may be created with a
 * blank-looking name only if it contains non-whitespace characters;
 * whitespace-only submissions are rejected" — and `FR-BOARD-010` AC3 — "given
 * an empty submission, when the user commits, then the rename is rejected and
 * the previous name remains". One primitive serves creation and rename, so the
 * two can never disagree about what "empty" means.
 *
 * Note what the trim does **not** license. `FR-BOARD-010`'s edge case ("long
 * list names truncate with an ellipsis in the header but remain fully editable
 * and readable on hover") is a rendering rule, not a storage rule: the full
 * title is stored and returned up to the column width, and the ellipsis is
 * `FE-11.2`'s. Truncating here would make the requirement unimplementable,
 * because the untruncated text it promises on hover would no longer exist.
 */
export const listTitleSchema = z.string().trim().min(1).max(LIST_TITLE_MAX_LENGTH);

export const LIST_COLOURS = [
  'none',
  'subtle_green',
  'subtle_yellow',
  'subtle_orange',
  'subtle_red',
  'subtle_purple',
  'subtle_blue',
  'subtle_sky',
  'subtle_lime',
  'subtle_pink',
  'subtle_grey',
] as const;

export const listColourSchema = z.enum(LIST_COLOURS);

export type ListColour = z.infer<typeof listColourSchema>;

/**
 * One end of the gap a list is being placed into — a neighbouring list's id, or
 * `null` for an open end.
 *
 * ⚠ **`previousListId` and `nextListId`, never `beforeListId`/`afterListId`.**
 * The naming matches `rankBetween(before, after)`'s argument order, and the
 * rejected spelling is rejected because it is ambiguous in the worst possible
 * way: "insert *before* X" and "the list *before* X" name opposite positions,
 * both readings are natural, and a caller that picks the wrong one gets a list
 * that lands on the wrong side of its neighbour with no error anywhere. Left
 * neighbour and right neighbour have no such reading.
 *
 * `.nullable().default(null)` on both, so an omitted key and an explicit `null`
 * are the same request. What that pair of nulls *means* differs between the two
 * bodies below, deliberately and loudly — see each.
 *
 * There is no `position` field on any body in this file, and that is D3 of the
 * block contract rather than an omission. `./rank`'s header states the rule
 * this enforces — "no caller should ever compute a rank by hand … the argument
 * order and the null convention are easy to get subtly wrong, and getting them
 * wrong is silent" — and a client-supplied rank would also make `FR-BOARD-011`
 * unverifiable, because two members dragging the same list converge only if one
 * authority computes both ranks from the same snapshot. The server resolves
 * both neighbours' ranks inside the transaction and calls `rankBetween` itself.
 */
const listNeighbourSchema = ulidSchema.nullable().default(null);

/**
 * `POST /lists` — `FR-BOARD-009`.
 *
 * The board is named **in the body**, not in the path, exactly as `POST
 * /boards` names its workspace: that is the shipped precedent for creating a
 * child of a container, and `GET /boards/:id/lists` hangs off the board prefix
 * exactly as `GET /workspaces/:id/boards` hangs off the workspace prefix. The
 * asymmetry is the same one B08 already settled and is not re-litigated here.
 *
 * ---------------------------------------------------------------------------
 * ⚠ BOTH NEIGHBOURS NULL MEANS "FAR RIGHT" HERE, AND MEANS SOMETHING ELSE ON
 * THE POSITION PATCH. READ BOTH BEFORE CHANGING EITHER.
 * ---------------------------------------------------------------------------
 * `FR-BOARD-009`'s behaviour text has two paths in one sentence each, and this
 * body serves both:
 *
 *   - "Submitting a name **creates the list at the far right**" — the composer
 *     at the end of the strip, which is the ordinary case and the one a client
 *     hits on every list it creates. That request carries neither neighbour, so
 *     both fields take their `null` default.
 *   - "A list can also be inserted **between two existing lists** by
 *     double-clicking the gap between them, which creates the new list at that
 *     position" — AC3's gap, which carries the two lists either side of it.
 *
 * So both-null does **not** mean `rankBetween(null, null)`. It means *append*:
 * the server reads the board's last live list and ranks the new list after it,
 * which collapses to `rankBetween(null, null)` only on a board that has no
 * lists yet. Reading the pair literally — "no neighbours, therefore an empty
 * list strip" — would put every new list at the **left** end of a populated
 * board, which is the exact opposite of the requirement, and it would do so
 * without an error: `rankBetween(null, null)` is a legal call that returns a
 * legal rank.
 *
 * An explicit `previousListId` with a null `nextListId` is the same append said
 * out loud, and an explicit `nextListId` with a null `previousListId` prepends.
 * The default is a shorthand for the first of those, not a fourth case.
 */
export const createListBodySchema = z.strictObject({
  boardId: ulidSchema.describe('The board the list is created on.'),
  title: listTitleSchema.describe(
    'The list name. Trimmed; a whitespace-only submission is refused (FR-BOARD-009).',
  ),
  previousListId: listNeighbourSchema.describe(
    'The list immediately to the LEFT of the gap the new list goes into. ' +
      'Omit both neighbours to create the list at the far right of the strip, ' +
      'which is FR-BOARD-009’s ordinary path; send both to insert into the gap ' +
      'between them (AC3’s double-click).',
  ),
  nextListId: listNeighbourSchema.describe(
    'The list immediately to the RIGHT of the gap the new list goes into. See ' +
      'previousListId.',
  ),
});

export type CreateListBody = z.infer<typeof createListBodySchema>;

/**
 * `PATCH /lists/:id/title` — `FR-BOARD-010`. SC-4, field-scoped.
 *
 * The title moves on its own and can move nothing else — **never a `PUT
 * /lists/:id`**, for the reasons `boardTitlePatchSchema` states in full and
 * which apply here unchanged: an absent key and a cleared value are the same
 * thing in the body a PUT receives, `FR-EXP-031`'s per-field last-write-wins
 * cannot be expressed against a whole-object write, and `FR-COLLAB-026`
 * requires that another member's edit to a different field must not clobber the
 * viewer's draft. `lists` carries a colour, an archive stamp and a delete stamp
 * that this route must not be able to touch, and two of the three have no owner
 * in this block at all.
 *
 * The position is a separate patch below for the same reason and not merely by
 * symmetry: a rename and a drag are different requirements with different
 * bodies, and the drag's body cannot contain a value the caller is allowed to
 * choose.
 */
export const listTitlePatchSchema = z.strictObject({
  title: listTitleSchema.describe(
    'The new list name. Trimmed; an empty or whitespace-only submission is ' +
      'refused and the previous name stands (FR-BOARD-010 AC3).',
  ),
});

export type ListTitlePatch = z.infer<typeof listTitlePatchSchema>;

export const listColourPatchSchema = z.strictObject({
  colour: listColourSchema.describe(
    'The list colour stored in lists.colour. Send none to clear the decoration.',
  ),
});

export type ListColourPatch = z.infer<typeof listColourPatchSchema>;

/** `PATCH /lists/:id/pr-list` — designate or clear the board's PR list. */
export const listPrListPatchSchema = z.strictObject({
  isPrList: z
    .boolean()
    .describe('true makes this the board PR list; false clears the flag.'),
});

export type ListPrListPatch = z.infer<typeof listPrListPatchSchema>;

/**
 * `PATCH /lists/:id/position` — `FR-BOARD-011`, "Dropping commits the new order
 * for every member". SC-4, field-scoped, and SC-2's fractional rank doing the
 * work.
 *
 * ---------------------------------------------------------------------------
 * ⚠ BOTH NEIGHBOURS NULL IS A 422 HERE, UNLIKE ON THE CREATE BODY ABOVE
 * ---------------------------------------------------------------------------
 * The two bodies share `listNeighbourSchema` and read the same pair of nulls
 * differently, on purpose. On creation, both-null is the far-right composer and
 * is the *common* case. Here it would mean "move this list to nowhere", which
 * is not a request anybody can have meant: a drag always lands somewhere, so a
 * position patch naming neither side of the gap it landed in is a client bug,
 * and the only alternative readings are "leave it where it is" (a silent
 * success for a mutation that did not happen) and "send it to one end" (a guess
 * about which end).
 *
 * Refusing it at the schema removes that ambiguity **without a round trip** and
 * without the handler having to carry a branch for it. One neighbour is enough
 * and is the honest shape of the two end positions: `{ nextListId }` alone
 * moves the list to the far left, `{ previousListId }` alone to the far right.
 *
 * The refusal message names both fields, which is required rather than
 * courteous: `app.ts`'s error handler maps only `issue.message` into `details`
 * — one convention for every 422 in the product — so a default refine message
 * would reach the caller as a refusal that never says what was omitted. The
 * same reasoning `boardVisibilityPatchSchema` records at `confirmWorldReadable`.
 *
 * What this body deliberately cannot express, beyond the missing `position`
 * (see `listNeighbourSchema`): a **board**. `FR-BOARD-012` — moving a list to
 * another board — is V1 and is not this block's; its own sentence says lists
 * "cannot be dragged from one board to another", so a reorder body that
 * accepted a `boardId` would be offering a path the requirement closes.
 */
export const listPositionPatchSchema = z
  .strictObject({
    previousListId: listNeighbourSchema.describe(
      'The list immediately to the LEFT of the gap this list is dropped into. ' +
        'Send this alone to move the list to the far right of the strip.',
    ),
    nextListId: listNeighbourSchema.describe(
      'The list immediately to the RIGHT of the gap this list is dropped into. ' +
        'Send this alone to move the list to the far left of the strip.',
    ),
  })
  .refine((body) => body.previousListId !== null || body.nextListId !== null, {
    error:
      'at least one of previousListId or nextListId must name a neighbouring list: ' +
      'send previousListId alone to move the list to the far right, nextListId ' +
      'alone to move it to the far left, or both to drop it into the gap between them.',
  });

export type ListPositionPatch = z.infer<typeof listPositionPatchSchema>;

/**
 * The two surfaces a board's lists are asked for — `FR-BOARD-015`,
 * `FR-EXP-010`.
 *
 * `open` is the board face: "Archiving a list … removes the list from the board
 * face". `archived` is the board menu's archived-items area, which `FR-EXP-010`
 * describes as listing "archived cards by default with a switch to view
 * archived lists" — this enum is that switch, on the list half of it.
 *
 * **No third member, and the missing one is `deleted`**, exactly as
 * `BOARD_LIST_STATES` has no `deleted` member: `FR-BOARD-016` requires a
 * permanently deleted list to be "gone from the archive and from search", so it
 * is absent from every read path rather than filterable by one. A state that
 * names it would be a state no query can serve.
 */
export const LIST_STATES = ['open', 'archived'] as const;

export const listStateSchema = z.enum(LIST_STATES);

export type ListState = z.infer<typeof listStateSchema>;

/**
 * `GET /boards/:id/lists?state=open|archived` — `FR-BOARD-015`, `FR-EXP-010`.
 *
 * `open` by default, because that is the board face and a caller who says
 * nothing means the ordinary surface. The same default `boardListQuerySchema`
 * takes, for the same reason.
 *
 * A `strictObject`, which is unusual for a query string and deliberate — and
 * the argument is `boardListQuerySchema`'s, inherited verbatim because the
 * failure is identical: `?status=archived` against a permissive schema strips
 * the unknown key, returns the **open** lists with a 200, and the caller reads
 * that as "this board has no archived lists". That is a wrong answer that looks
 * like a right one, on the exact surface `FR-EXP-010` exists to make things
 * recoverable from. The cost is that an unknown key — a link tracker's
 * `utm_source`, a proxy's addition — is a 422; acceptable, because these paths
 * are called by code, not typed into an address bar.
 */
export const listStateQuerySchema = z.strictObject({
  state: listStateSchema.default('open'),
});

export type ListStateQuery = z.infer<typeof listStateQuerySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — see the note above
// the input primitives, and `./user`'s longer version of the same argument.
// ---------------------------------------------------------------------------

/**
 * A list as every route in B11 that answers with a body returns it — `POST
 * /lists`, `GET /boards/:id/lists`, `PATCH /lists/:id/title`,
 * `PATCH /lists/:id/position`, `POST /lists/:id/archive` and
 * `POST /lists/:id/restore`. (`DELETE /lists/:id` answers `204` with no body,
 * so it needs no shape — `FR-PERM-029`'s removal route set the precedent.)
 *
 * One view for all six, rather than a narrower shape per route, because they
 * all answer the same question — *what is this list now* — and a client that
 * has just dragged a list needs the same fields as one that has just created
 * it. The alternative, a `ListSummary` for the strip and a `List` for the
 * mutations, would be two shapes that must be kept identical by hand.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LIFECYCLE IS ONE BOOLEAN
 * ---------------------------------------------------------------------------
 * `lists` carries `archived_at` and `deleted_at`, both nullable
 * `timestamptz(3)`, and neither reaches the wire. `boardSchema`'s lifecycle
 * note is the long form of the argument and it transfers intact; the short
 * form is that `archived` is the whole of what a caller can act on (show the
 * list on the board face, or list it under archived items with a "Send to
 * board" action), and `deletedAt` could only ever be `null` because a deleted
 * list is absent from every read path.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `createdAt` AND `updatedAt` ARE THE ONE PLACE THIS FILE DIVERGES FROM
 * `./board`, AND THE DIVERGENCE IS ON RECORD RATHER THAN ARGUED AWAY
 * ---------------------------------------------------------------------------
 * `boardSchema` serves no timestamp at all and says why at length: a timestamp
 * on the wire is a field the web client may bind to and a later block may not
 * remove, and no requirement asked for one. **The same is true here** — no
 * acceptance criterion in `FR-BOARD-009` through `FR-BOARD-016` names a list's
 * creation or modification time, and the nearest thing to one,
 * `FR-BOARD-013`'s "date created" sort, orders a list's *cards* rather than the
 * lists themselves. These two fields are carried because the block contract
 * fixes this view's field set and five specs are written against it; they are
 * not carried because a requirement in this block needs them.
 *
 * Stated plainly so that the next block does not read their presence as a
 * precedent for adding more: **a field is added here when a route needs it**,
 * which is the rule the rest of this package follows, and these two are the
 * exception rather than the pattern.
 */
export const listSchema = z.object({
  id: ulidSchema,
  /**
   * `lists.board_id char(26) NOT NULL`, `fk_lists_board_id` referencing
   * `boards(id)`.
   *
   * Never null, and the column is where that is enforced — so unlike a card
   * (`cards.board_id` is nullable, because a card in a personal inbox belongs
   * to no board) a list without a board is **inexpressible**. That is what
   * makes every list route resolvable to exactly one board, which is what every
   * permission guard in this product needs: guards take a board id, and a list
   * that could be board-less would have no scope to be judged in.
   */
  boardId: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  colour: listColourSchema,
  /**
   * `lists.is_pr_list` — whether this is the board's single PR list (cards in
   * it carry approvals). At most one list per board; written by
   * `PATCH /lists/:id/pr-list`.
   */
  isPrList: z.boolean(),
  /**
   * `lists.position varchar(64) COLLATE "C"` — the fractional rank the strip is
   * ordered by, `RANK_MAX_LENGTH` from `./rank`.
   *
   * **Opaque.** It is on the wire because a client rendering the strip needs to
   * know the key it was ordered by — to hold a stable order across an
   * optimistic drag, and to tell a locally reordered list from a reordered one
   * that arrived over the socket. It is emphatically **not** on the wire so a
   * client can compute one: `listPositionPatchSchema` takes neighbours and no
   * client may send a rank, for the reasons `listNeighbourSchema` records.
   *
   * Compare it with `compareRanks` from `./rank` and nothing else.
   * `localeCompare` is not byte order and would sort a strip differently from
   * the database that produced it — a divergence with no error attached.
   */
  position: z.string().max(RANK_MAX_LENGTH),
  /**
   * `lists.archived_at IS NOT NULL` — `FR-BOARD-015`. Mirrors
   * `boardSchema.closed`.
   *
   * ⚠ **This says nothing about the list's cards, and that is the correctness
   * trap of this block rather than an omission.** `FR-BOARD-015`'s edge case:
   * "Archiving a list is distinct from archiving its cards: restoring the list
   * restores the cards it held, whereas cards archived individually before the
   * list was archived stay archived." Archiving a list therefore **must not**
   * write `cards.archived_at` — if it did, restore could not tell the two
   * populations apart and the edge case would be unimplementable. The list's
   * own flag is what hides its cards from the board face, which means the card
   * read paths (`BE-12.x`, `BE-13.1`) must filter on the parent list's
   * `archived_at IS NULL` as well as the card's own.
   *
   * ⚠ **"none of which exist yet" is corrected in place rather than deleted:
   * both exist and both filter.** `GET /lists/:id/cards` (B12) and
   * `GET /boards/:id/contents` (B13) each carry the conjunct, B13 being the
   * last place the bill was owed. What has *not* changed is why no schema in
   * this package can enforce it: `cardSchema` carries no field expressing "my
   * list is archived", because a card in an archived list legitimately reports
   * `archived: false` about **itself**. The conjunct is pinned in compiled SQL
   * and against live rows, and nowhere here.
   */
  archived: z.boolean(),
  /**
   * How many cards belong to this list — `lists`' cards with `deleted_at IS
   * NULL`, **archived and open alike**.
   *
   * The "alike" is the decision, and it comes straight out of `FR-BOARD-016`:
   * the delete confirmation "requires confirmation and states that the list and
   * all of its cards — **open and archived alike** — are deleted irreversibly".
   * A client cannot state that number without being told it, and the
   * alternative was a body flag on `DELETE /lists/:id` carrying an
   * acknowledgement of a count the server never disclosed. Making it a **read**
   * means the confirmation dialog is built from what the archive surface
   * already returned, and the number it shows is the number the delete will
   * take.
   *
   * ⚠ It is therefore **not** the badge a board-face renderer wants. "How many
   * open cards are in this list" is a different number — archived cards
   * excluded — and the block that builds the board face (`BE-13.1`) should add
   * that field beside this one rather than redefining this one, because
   * `FR-BOARD-016`'s sentence would then have nothing to read. **B13 did
   * exactly that**: `boardFaceListSchema` in `./board-contents` is
   * `listSchema.extend({ openCardCount, cards })`, so the board face reads its
   * own number and this one keeps its meaning on all seven of B11's routes.
   * ⚠ `listSchema` itself was **not** widened, and must not be: it is the
   * response serialiser of every one of those routes, and adding `cards` to it
   * would turn `GET /boards/:id/lists` into the board read four docstrings say
   * it must never become.
   *
   * ⚠ **"It is `0` for every list today" stood here through B11 and is
   * corrected rather than deleted, because the reason it was true is the
   * reason a real bug survived.** `POST /cards` (B12) landed, so the count
   * moves; while it could not, the correlated subquery behind it was
   * unassertable against anything but zero, and an `InitPlan` decorrelation
   * that made it constant `0` shipped and stood for a whole block. A field no
   * test can move is a field no test is checking.
   */
  cardCount: z.number().int().nonnegative(),
  /**
   * `lists.created_at`, serialised as the driver returns it — a bounded string
   * rather than `z.iso.datetime()` or a coerced `Date`, for the reason
   * `workspaceMemberSchema.joinedAt` states in full: the column is
   * `timestamptz(3)` and `pg` hands it back in Postgres' own spelling
   * (`2026-08-10 12:00:00.123+00`), a legitimate instant and not an ISO 8601
   * `T`/`Z` form, so pinning the serialisation here would make this schema
   * reject the value the database actually produced.
   *
   * See the note above on why these two fields are here at all.
   */
  createdAt: z.string().min(1),
  /**
   * `lists.updated_at`, same serialisation note as `createdAt`.
   *
   * Written by `trg_lists_set_updated_at`, never by a route. A second writer of
   * a trigger-derived column is the mistake `users.email_verified_at` exists to
   * avoid, and B07 recorded the same rule for `workspaces.updated_at`.
   */
  updatedAt: z.string().min(1),
});

export type List = z.infer<typeof listSchema>;

/**
 * `GET /boards/:id/lists` — a board's lists in strip order.
 *
 * Ordered by the server, `ORDER BY position, id` (`./rank`'s rule 2 — the index
 * on `position` is deliberately non-unique, so `position` alone lets the planner
 * return ties in any order, and a drag race is exactly how a tie appears). A
 * client renders the array as received and does not re-sort it.
 */
export const listListSchema = z.array(listSchema);
